<script lang="ts">
    import Editor from 'cl-editor';
    import type {FormControlOptions} from '../../forms/types';
    import type {HtmlValueEditorOptions} from './types';
    import type {TextValueEditorValidations} from '../text/types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {SvelteComponentConstructor} from 'core/types';
    import type {CommonValueEditorLocalizations} from 'shared/value-editors/internal/editors/_shared/common-types';
    import type {SvelteComponent} from 'svelte';
    import type {FormControl} from '../../forms/form-control';
    import type {ValueEditorSize} from '../../../types';
    import {FormControlBuilder} from '../../forms/form-control';
    import {emptyAsNullFormatter, emptyAsNullParser} from '../_shared/editors-utils';
    import {createInputDescription} from '../../description/utils';
    import {getContext, onDestroy, onMount, tick} from 'svelte';
    import {sanitizeHtmlFormatter, sanitizeHtmlParser, validatorFactory} from './utils';
    import {debounceTime, distinctUntilChanged, filter, map} from 'rxjs/operators';
    import {merge, Subject} from 'rxjs';
    import {touched} from '../../shared/use.touched';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {style} from 'svelte-forms';
    import {ariaLabelledby} from '../../label/use.aria-labelledby';
    import {getValueEditorLabelContext} from '../../label/value-editor-label-context';
    import {getLogger, getSanitize} from 'core/svelte-context/context';
    import {disabledAttribute} from '../../shared/use.disabled-attribute';
    import {ignoreUnusedProperties} from 'shared/utils/custom-utils';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import Description from '../../description/Description.svelte';

    // cast svelte component of Editor to silence svelte-check errors
    const ClEditor = Editor as unknown as SvelteComponent & SvelteComponentConstructor<any>;

    export let model: string;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string; // html editor does not support placeholder

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: HtmlValueEditorOptions;
    export let validations: TextValueEditorValidations;

    ignoreUnusedProperties(placeholder);

    let editorInstance: SvelteComponent<Editor>;
    const RESET_SYMBOL = Symbol('reset-notification');
    const resetNotification$ = new Subject();
    const blurEvent$ = new Subject<any>();
    const inputEvent$ = new Subject<any>();
    const localize: EditorsLocalizationFunction<CommonValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);
    const sanitizeHtml = getSanitize();
    const labelContext = getValueEditorLabelContext();


    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withValidators(validatorFactory(validations))
        .withOptions(formControlOptions)
        .withParsers([sanitizeHtmlParser(sanitizeHtml, options)])
        .withModifiers([emptyAsNullParser(options)])
        .withFormatters([sanitizeHtmlFormatter(sanitizeHtml), emptyAsNullFormatter(options)])
        .build(getLogger());


    const modelValueSubscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    let currentViewValue = '';
    const viewValueSubscription = formControl.getViewValue$().subscribe((viewValue) => renderViewValue(viewValue));

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    const inputValueStreams = [];
    formControl.formControlOptions.updateTriggers.forEach(({eventName, debounceValue = 0}) => {
        if (eventName === 'input') {
            inputValueStreams.push(inputEvent$.pipe(map(() => extractCurrentViewValue()), debounceTime(debounceValue)));
        }
        if (eventName === ' blur') {
            inputValueStreams.push(blurEvent$.pipe(map(() => extractCurrentViewValue()), debounceTime(debounceValue)));
        }
    });

    const inputValuesSubscription = merge(...inputValueStreams, resetNotification$)
        .pipe(
            distinctUntilChanged(),
            filter((value) => value !== RESET_SYMBOL),
            filter((value) => value === extractCurrentViewValue())) // ignore debounced value if model was changed in the meantime from outside
        .subscribe((value) => formControl.setViewValue(value));

    $: toggleDisabledState(isDisabled);

    onMount(() => {
        toggleDisabledState(isDisabled);
        if (isFocused && !isDisabled) {
            editorInstance.refs.editor.focus();
        }
    });

    onDestroy(() => {
        modelValueSubscription.unsubscribe();
        viewValueSubscription.unsubscribe();
        inputValuesSubscription.unsubscribe();
    });

    async function toggleDisabledState(disable: boolean) {
        if (!editorInstance) {
            return;
        }

        await tick(); // wait till all elements of editor are rendered
        if (disable) {
            editorInstance.refs.editor.contentEditable = 'false';
            editorInstance.refs.editor.parentElement
                .querySelector('.cl-actionbar')
                .querySelectorAll('button')
                .forEach((button) => button.disabled = true);
        } else {
            editorInstance.refs.editor.contentEditable = 'true';
            editorInstance.refs.editor.parentElement
                .querySelector('.cl-actionbar')
                .querySelectorAll('button')
                .forEach((button) => button.disabled = false);
        }
    }

    function renderViewValue(viewValue: string) {
        currentViewValue = viewValue ?? '';
        if (sanitizeHtml(editorInstance?.getHtml()) !== currentViewValue) {
            editorInstance?.setHtml(currentViewValue);
            resetNotification$.next(RESET_SYMBOL);
        }
    }

    function extractCurrentViewValue() {
        return editorInstance.getHtml();
    }

    function onChange(event: CustomEvent<string>) {
        inputEvent$.next(event);
    }

    function onBlur(event: FocusEvent) {
        blurEvent$.next(event);
    }

    function initializeActions() {
        const CUSTOM_ACTIONS = {
            h3: {
                name: 'h3',
                icon: '<b>H<sub>3</sub></b>',
                title: 'Heading 3',
                result: () => editorInstance.exec('formatBlock', '<H3>')
            },
            h4: {
                name: 'h4',
                icon: '<b>H<sub>4</sub></b>',
                title: 'Heading 4',
                result: () => editorInstance.exec('formatBlock', '<H4>')
            }
        };

        return options.editorOptions.actions.map((action) => action in CUSTOM_ACTIONS ? CUSTOM_ACTIONS[action] : action);
    }

    export function getFormController(): FormControl<string> {
        return formControl;
    }
</script>

<div class="form-control input-{size} cl-editor-wrapper"
     id={editorId}
     data-name={editorName}

     use:disabledAttribute={isDisabled}
     use:touched={{formControl, touchTriggerEvent: 'focusout'}}
     use:style={{field: formControl.getFieldStateAsStore()}}
     use:ariaInvalid={formControl}
     use:ariaErrormessage={formControl}
     use:ariaDescribedby={inputDescription}
     use:ariaLabelledby={labelContext}
     data-main-input>

    <ClEditor bind:this={editorInstance} html={currentViewValue} actions={initializeActions()} on:blur={onBlur} on:change={onChange}/>
</div>

<ErrorMessages formController={formControl} {forceShowErrors} {size}/>
<Description {inputDescription}/>

<style lang="less" global>
    .form-control.cl-editor-wrapper {
        padding: 0;
        height: auto;

        &[disabled] .cl-content {
            background-color: transparent;
        }

        .cl { // disable shadow of cl-editor wrapper
            box-shadow: none;
        }
    }
</style>